/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type CSSProperties, type FC, type ReactNode, useCallback, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Root as DialogRoot,
  Portal as DialogPortal,
  Overlay as DialogOverlay,
  Content as DialogContent,
  Title as DialogTitle,
  Close as DialogClose,
} from "@radix-ui/react-dialog";
import { Drawer } from "vaul";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { CloseIcon } from "@vector-im/compound-design-tokens/assets/web/icons";
import classNames from "classnames";
import { Heading, Glass } from "@vector-im/compound-web";

import styles from "./Modal.module.css";
import overlayStyles from "./Overlay.module.css";
import { useMediaQuery } from "./useMediaQuery";
import { useVisualViewport } from "./useVisualViewport";

export interface Props {
  title: string;
  /**
   * Hide the modal header. Used for smaller popups where the context is readily apparent.
   * A title should still be specified for users using assistive technology.
   */
  hideHeader?: boolean;
  children: ReactNode;
  className?: string;
  /**
   * Class name to be used when in drawer mode (touchscreen).
   */
  classNameDrawer?: string;
  /**
   * Class name to be used when in modal mode (desktop).
   */
  classNameModal?: string;
  /**
   * The controlled open state of the modal.
   */
  // An option to leave the open state uncontrolled is intentionally not
  // provided, since modals are always opened due to external triggers, and it
  // is the author's belief that controlled components lead to more obvious code.
  open: boolean;
  /**
   * Callback for when the user dismisses the modal. If undefined, the modal
   * will be non-dismissable.
   */
  onDismiss?: () => void;
  /**
   * Whether the modal content has tabs.
   */
  // TODO: Better tabs support
  tabbed?: boolean;
  /** Fit form dialogs above the phone keyboard without drawer repositioning. */
  fitVisualViewport?: boolean;
}

/**
 * A modal, taking the form of a drawer / bottom sheet on touchscreen devices,
 * and a dialog box on desktop.
 */
export const Modal: FC<Props> = ({
  title,
  hideHeader,
  children,
  className,
  classNameDrawer,
  classNameModal,
  open,
  onDismiss,
  tabbed,
  fitVisualViewport = false,
  ...rest
}) => {
  const { t } = useTranslation();
  // Empirically, Chrome on Android can end up not matching (hover: none), but
  // still matching (pointer: coarse) :/
  const touchscreen = useMediaQuery("(hover: none) or (pointer: coarse)");
  const landscape = useMediaQuery("(orientation: landscape) and (min-width: 550px)");
  const viewportFit = touchscreen && fitVisualViewport;
  const viewport = useVisualViewport(open && viewportFit);
  const dialogRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open || !viewportFit) return;
    const focused = document.activeElement;
    const dialog = dialogRef.current;
    if (!(focused instanceof HTMLElement) || !dialog?.contains(focused)) return;
    // Scroll only the category body, never the dialog or the page behind it.
    const scroller = focused.closest<HTMLElement>('[role="tabpanel"]') ??
      dialog.querySelector<HTMLElement>(`.${styles.body}`);
    if (!scroller) return;
    const field = focused.getBoundingClientRect();
    const bounds = scroller.getBoundingClientRect();
    if (field.bottom > bounds.bottom - 12)
      scroller.scrollTop += field.bottom - bounds.bottom + 12;
    else if (field.top < bounds.top + 12)
      scroller.scrollTop -= bounds.top + 12 - field.top;
  }, [open, viewportFit, viewport.height, viewport.top]);
  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onDismiss?.();
    },
    [onDismiss],
  );

  if (touchscreen && !viewportFit) {
    return (
      <Drawer.Root
        open={open}
        // This autofocus is a custom vault property and not the
        // standard HTML autofocus attribute.
        // It makes the Drawer.Root behave like the `DialogRoot`
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        onOpenChange={onOpenChange}
        dismissible={onDismiss !== undefined}
      >
        <Drawer.Portal>
          <Drawer.Overlay className={classNames(overlayStyles.bg)} />
          <Drawer.Content
            className={classNames(
              className,
              classNameDrawer,
              overlayStyles.overlay,
              styles.modal,
              styles.drawer,
              { [styles.tabbed]: tabbed },
            )}
            role="dialog"
            // Suppress the warning about there being no description; the modal
            // has an accessible title
            aria-describedby={undefined}
            {...rest}
          >
            <div className={styles.content}>
              <div className={styles.header}>
                <div className={styles.handle} />
                {hideHeader ? (
                  <VisuallyHidden asChild>
                    <Drawer.Title>{title}</Drawer.Title>
                  </VisuallyHidden>
                ) : (
                  <>
                    <Drawer.Title asChild>
                      <Heading as="h2" weight="semibold" size="md">
                        {title}
                      </Heading>
                    </Drawer.Title>
                    {onDismiss !== undefined && (
                      <Drawer.Close
                        className={styles.close}
                        aria-label={t("action.close")}
                      >
                        <CloseIcon width={20} height={20} />
                      </Drawer.Close>
                    )}
                  </>
                )}
              </div>
              <div className={styles.body}>{children}</div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    );
  } else {
    const titleNode = (
      <DialogTitle asChild>
        <Heading as="h2" weight="semibold" size="md">
          {title}
        </Heading>
      </DialogTitle>
    );
    const header = (
      <div className={styles.header}>
        {titleNode}
        {onDismiss !== undefined && (
          <DialogClose
            className={styles.close}
            data-testid="modal_close"
            aria-label={t("action.close")}
          >
            <CloseIcon width={20} height={20} />
          </DialogClose>
        )}
      </div>
    );

    return (
      <DialogRoot open={open} onOpenChange={onOpenChange}>
        <DialogPortal>
          <DialogOverlay
            className={classNames(overlayStyles.bg, overlayStyles.animate)}
          />
          <DialogContent
            ref={dialogRef}
            style={viewportFit ? {
              "--modal-viewport-height": `${viewport.height}px`,
              "--modal-viewport-top": `${viewport.top}px`,
            } as CSSProperties : undefined}
            data-viewport-fit={viewportFit || undefined}
            data-compact={viewportFit && viewport.height < 420 || undefined}
            data-landscape={viewportFit && landscape || undefined}
            data-tiny={viewportFit && viewport.height < 180 || undefined}
            data-keyboard-open={viewportFit && viewport.height < Math.max(window.innerHeight, document.documentElement.clientHeight) - 100 || undefined}
            asChild
            // Suppress the warning about there being no description; the modal
            // has an accessible title
            aria-describedby={undefined}
            role="dialog"
            {...rest}
          >
            <Glass
              className={classNames(
                className,
                classNameModal,
                overlayStyles.overlay,
                overlayStyles.animate,
                styles.modal,
                styles.dialog,
                { [styles.tabbed]: tabbed, [styles.viewportDialog]: viewportFit },
              )}
            >
              <div className={styles.content}>
                {!hideHeader ? header : null}
                {hideHeader ? (
                  <VisuallyHidden asChild>{titleNode}</VisuallyHidden>
                ) : null}
                <div className={styles.body}>{children}</div>
              </div>
            </Glass>
          </DialogContent>
        </DialogPortal>
      </DialogRoot>
    );
  }
};
